import styled, { keyframes } from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Loader } from '@shared/ui/Progress';
import { useGetStatutMetricsQuery } from '@shared/api';
import type { BackupHealth } from '@entities/legal/types';
import {
  backupAgo,
  backupDumpSub,
  backupKindTone,
  backupOffsiteSub,
  backupRestoreSub,
  backupStateLabel,
  backupStateTone,
} from '@entities/legal/backupHealth';

const Note = styled.div`
  background: ${({ theme }) => theme.colors.primarySoft};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  line-height: 1.5;
`;

const SectionTitle = styled.h3`
  margin: ${({ theme }) => theme.spacing(5)} 0 ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.muted};

  &:first-of-type {
    margin-top: 0;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const reveal = keyframes`
 from { opacity: 0; transform: translateY(6px); }
 to { opacity: 1; transform: translateY(0); }
`;

interface MetricBoxProps {
  $tone: 'ok' | 'warn' | 'danger' | 'neutral';
}

const MetricBox = styled(Card)<MetricBoxProps>`
  position: relative;
  padding: ${({ theme }) => theme.spacing(3.5)};
  animation: ${reveal} 320ms ${({ theme }) => theme.motion.easeOut} both;
  border-left: 3px solid
    ${({ $tone, theme }) =>
      $tone === 'ok'
        ? theme.colors.status.success
        : $tone === 'warn'
          ? theme.colors.status.warning
          : $tone === 'danger'
            ? theme.colors.status.danger
            : theme.colors.border};
`;

const Caption = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Big = styled.div<{ $tone: MetricBoxProps['$tone'] }>`
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  color: ${({ $tone, theme }) =>
    $tone === 'ok'
      ? theme.colors.status.success
      : $tone === 'warn'
        ? theme.colors.status.warning
        : $tone === 'danger'
          ? theme.colors.status.danger
          : theme.colors.text.primary};
`;

const Sub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 6px;
`;

const Target = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 4px;
`;

const Track = styled.div`
  margin-top: 10px;
  height: 6px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 3px;
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number; $tone: MetricBoxProps['$tone'] }>`
  width: ${({ $pct }) => Math.max(0, Math.min(100, $pct))}%;
  height: 100%;
  background: ${({ $tone, theme }) =>
    $tone === 'ok'
      ? theme.colors.status.success
      : $tone === 'warn'
        ? theme.colors.status.warning
        : $tone === 'danger'
          ? theme.colors.status.danger
          : theme.colors.primary};
  transition: width 400ms ${({ theme }) => theme.motion.easeOut};
`;

function fmtPct(v: number): string {
  return `${v.toFixed(1)}%`;
}

const Problems = styled.ul`
  margin: ${({ theme }) => theme.spacing(3)} 0 0;
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(3)}
    ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(6)};
  background: ${({ theme }) => theme.colors.surface};
  border-left: 3px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  line-height: 1.5;

  li + li {
    margin-top: 4px;
  }
`;

interface MetricCardProps {
  caption: string;
  value: string;
  tone: MetricBoxProps['$tone'];
  sub?: string;
  target?: string;
  progressPct?: number;
}

function MetricCard({ caption, value, tone, sub, target, progressPct }: MetricCardProps) {
  return (
    <MetricBox $tone={tone}>
      <Caption>{caption}</Caption>
      <Big $tone={tone}>{value}</Big>
      {sub && <Sub>{sub}</Sub>}
      {target && <Target>Цель: {target}</Target>}
      {progressPct != null && (
        <Track>
          <Fill $pct={progressPct} $tone={tone} />
        </Track>
      )}
    </MetricBox>
  );
}

/**
 * Резервное копирование базы (Б-1): состояние по журналу ops_backup_runs.
 * Сбой здесь же уходит администраторам в колокольчик (раз в час, без повторов).
 */
function BackupSection({ health }: { health: BackupHealth }) {
  const stateTone = backupStateTone(health.state);
  return (
    <>
      <SectionTitle>Резервное копирование базы</SectionTitle>
      <Grid>
        <MetricCard
          caption="Состояние"
          value={backupStateLabel(health.state)}
          tone={stateTone}
          sub={
            health.state === 'not_configured'
              ? 'копирование не включено — копий нет'
              : health.problems.length > 0 && health.state !== 'unknown'
                ? `проблем: ${health.problems.length}`
                : health.state === 'ok'
                  ? 'копия свежая, вынесена, восстановление проверено'
                  : undefined
          }
        />
        {health.dump && (
          <MetricCard
            caption="Копия на сервере"
            value={backupAgo(health.dump.age_hours)}
            tone={backupKindTone(health, 'dump')}
            sub={backupDumpSub(health.dump)}
            target={
              health.dump_max_age_hours ? `не старше ${health.dump_max_age_hours} ч` : undefined
            }
          />
        )}
        {health.offsite && (
          <MetricCard
            caption="Копия вне сервера"
            value={backupAgo(health.offsite.age_hours)}
            tone={backupKindTone(health, 'offsite')}
            sub={backupOffsiteSub(health.offsite, health.offsite_mode)}
            target={
              health.offsite_max_age_hours
                ? `не старше ${health.offsite_max_age_hours} ч`
                : undefined
            }
          />
        )}
        {health.restore_test && (
          <MetricCard
            caption="Проверка восстановления"
            value={backupAgo(health.restore_test.age_hours)}
            tone={backupKindTone(health, 'restore_test')}
            sub={backupRestoreSub(health.restore_test)}
            target={
              health.restore_max_age_days
                ? `не реже раза в ${health.restore_max_age_days} сут`
                : undefined
            }
          />
        )}
      </Grid>
      {health.problems.length > 0 && health.state !== 'ok' && (
        <Problems>
          {health.problems.map((p) => (
            <li key={p.code}>{p.text}</li>
          ))}
        </Problems>
      )}
    </>
  );
}

/**
 * Дашборд метрик «Целевые показатели качества».
 * Считается «как есть сейчас», без LLM-вызовов — одним запросом к БД.
 */
export function Metrics() {
  const { data, isLoading } = useGetStatutMetricsQuery(undefined, {
    pollingInterval: 30_000,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
  });

  if (isLoading || !data) return <Loader />;

  const acceptanceTone: MetricBoxProps['$tone'] =
    data.contracts.risks_annotated === 0
      ? 'neutral'
      : data.contracts.acceptance_rate_pct >= data.contracts.target_acceptance_pct
        ? 'ok'
        : data.contracts.acceptance_rate_pct >= 40
          ? 'warn'
          : 'danger';

  const refsTone: MetricBoxProps['$tone'] =
    data.law_refs.total === 0
      ? 'neutral'
      : data.law_refs.accuracy_pct >= data.law_refs.target_accuracy_pct
        ? 'ok'
        : data.law_refs.accuracy_pct >= 90
          ? 'warn'
          : 'danger';

  const classificationTone: MetricBoxProps['$tone'] =
    data.correspondence.total === 0
      ? 'neutral'
      : data.correspondence.classification_rate_pct >= data.correspondence.target_classification_pct
        ? 'ok'
        : 'warn';

  const rerouteTone: MetricBoxProps['$tone'] =
    data.correspondence.total === 0
      ? 'neutral'
      : data.correspondence.reroute_rate_pct <= data.correspondence.target_reroute_max_pct
        ? 'ok'
        : 'danger';

  const overdueTone: MetricBoxProps['$tone'] = data.deadlines.overdue > 0 ? 'danger' : 'ok';

  return (
    <>
      <Note>
        Сводные метрики качества работы юр.агента по разделу Считаются прямо из БД — данные
        актуальны на момент открытия страницы (обновляются автоматически раз в 30 сек). Цветовая
        индикация показывает отношение текущего значения к целевому из.
      </Note>

      {data.backups && <BackupSection health={data.backups} />}

      <SectionTitle>Договорная работа (, HITL)</SectionTitle>
      <Grid>
        <MetricCard
          caption="% рисков принято юристом"
          value={fmtPct(data.contracts.acceptance_rate_pct)}
          tone={acceptanceTone}
          sub={`${data.contracts.risks_accepted} из ${data.contracts.risks_annotated} размеченных`}
          target={`≥ ${data.contracts.target_acceptance_pct}%`}
          progressPct={data.contracts.acceptance_rate_pct}
        />
        <MetricCard
          caption="Всего договоров"
          value={String(data.contracts.total)}
          tone="neutral"
          sub={`Анализируется: ${data.contracts.under_review} · Готово: ${data.contracts.analyzed}`}
        />
        <MetricCard
          caption="Рисков найдено LLM"
          value={String(data.contracts.risks_total)}
          tone="neutral"
          sub={`Под разметку: ${data.contracts.risks_total - data.contracts.risks_annotated}`}
        />
        <MetricCard
          caption="Отклонено / спорно"
          value={`${data.contracts.risks_rejected} / ${data.contracts.risks_disputed}`}
          tone="neutral"
          sub="не идут в протокол разногласий"
        />
      </Grid>

      <SectionTitle>Точность ссылок на НПА ()</SectionTitle>
      <Grid>
        <MetricCard
          caption="% ссылок подтверждено в KB"
          value={fmtPct(data.law_refs.accuracy_pct)}
          tone={refsTone}
          sub={`${data.law_refs.verified} из ${data.law_refs.total} ссылок`}
          target={`≥ ${data.law_refs.target_accuracy_pct}%`}
          progressPct={data.law_refs.accuracy_pct}
        />
        <MetricCard
          caption="KB: кодексы / ФЗ / Пленумы"
          value={String(data.knowledge_base.codes_fz_plenum)}
          tone={data.knowledge_base.codes_fz_plenum > 0 ? 'ok' : 'warn'}
          sub="загружено в базу знаний"
        />
        <MetricCard
          caption="KB: ГОСТ / СНиП / СП"
          value={String(data.knowledge_base.tech_norms)}
          tone={data.knowledge_base.tech_norms > 0 ? 'ok' : 'neutral'}
          sub="для compliance-проверки ПД"
        />
        <MetricCard
          caption="KB: эталонные формы"
          value={String(data.knowledge_base.contract_templates)}
          tone={data.knowledge_base.contract_templates > 0 ? 'ok' : 'neutral'}
          sub="корпоративные стандарты"
        />
      </Grid>

      <SectionTitle>Корреспонденция ()</SectionTitle>
      <Grid>
        <MetricCard
          caption="% классифицировано"
          value={fmtPct(data.correspondence.classification_rate_pct)}
          tone={classificationTone}
          sub={`${data.correspondence.classified} из ${data.correspondence.total} писем`}
          target={`≥ ${data.correspondence.target_classification_pct}%`}
          progressPct={data.correspondence.classification_rate_pct}
        />
        <MetricCard
          caption="% возвращено диспетчеру"
          value={fmtPct(data.correspondence.reroute_rate_pct)}
          tone={rerouteTone}
          sub={`${data.correspondence.returned} писем`}
          target={`≤ ${data.correspondence.target_reroute_max_pct}%`}
          progressPct={data.correspondence.reroute_rate_pct}
        />
        <MetricCard
          caption="Всего входящих"
          value={String(data.correspondence.total)}
          tone="neutral"
        />
      </Grid>

      <SectionTitle>Сроки (— 0 пропусков)</SectionTitle>
      <Grid>
        <MetricCard
          caption="Просрочено"
          value={String(data.deadlines.overdue)}
          tone={overdueTone}
          sub={
            data.deadlines.overdue === 0
              ? '✓ Цель достигнута'
              : 'Требует немедленного внимания юриста'
          }
          target="0"
        />
        <MetricCard
          caption="Активных сроков"
          value={String(data.deadlines.pending)}
          tone="neutral"
          sub={`Отправлено напоминаний: ${data.deadlines.notified}`}
        />
        <MetricCard caption="Всего" value={String(data.deadlines.total)} tone="neutral" />
      </Grid>

      <SectionTitle>Претензии и иски (HITL)</SectionTitle>
      <Grid>
        <MetricCard
          caption="% замечаний принято юристом"
          value={fmtPct(data.claims.acceptance_rate_pct)}
          tone={
            data.claims.risks_annotated === 0
              ? 'neutral'
              : data.claims.acceptance_rate_pct >= 60
                ? 'ok'
                : data.claims.acceptance_rate_pct >= 40
                  ? 'warn'
                  : 'danger'
          }
          sub={`${data.claims.risks_accepted} из ${data.claims.risks_annotated} размеченных`}
          target="≥ 60%"
          progressPct={data.claims.acceptance_rate_pct}
        />
        <MetricCard
          caption="Всего замечаний"
          value={String(data.claims.risks_total)}
          tone="neutral"
          sub={`Под разметку: ${data.claims.risks_total - data.claims.risks_annotated}`}
        />
        <MetricCard
          caption="Отклонено / спорно"
          value={`${data.claims.risks_rejected} / ${data.claims.risks_disputed}`}
          tone="neutral"
          sub="не учитываются в позиции"
        />
      </Grid>

      <SectionTitle>Качество HITL (где LLM пропускает)</SectionTitle>
      <Grid>
        <MetricCard
          caption="Свой риск (договоры) · 7 дней"
          value={String(data.hitl_quality.manual_contract_risks_7d)}
          tone={data.hitl_quality.manual_contract_risks_7d > 5 ? 'warn' : 'neutral'}
          sub={`Всего вручную добавлено: ${data.hitl_quality.manual_contract_risks_total}`}
        />
        <MetricCard
          caption="Свой риск (претензии) · 7 дней"
          value={String(data.hitl_quality.manual_claim_risks_7d)}
          tone={data.hitl_quality.manual_claim_risks_7d > 5 ? 'warn' : 'neutral'}
          sub={`Всего вручную: ${data.hitl_quality.manual_claim_risks_total}`}
        />
        <MetricCard
          caption="Юрист правил текст (договоры)"
          value={String(data.hitl_quality.lawyer_corrected_contract_total)}
          tone={
            data.hitl_quality.lawyer_corrected_contract_total > 20 ? 'warn' : 'neutral'
          }
          sub="LLM-формулировка нуждалась в правке"
        />
        <MetricCard
          caption="Юрист правил текст (претензии)"
          value={String(data.hitl_quality.lawyer_corrected_claim_total)}
          tone={data.hitl_quality.lawyer_corrected_claim_total > 20 ? 'warn' : 'neutral'}
        />
        <MetricCard
          caption="Ждут разметки (договоры)"
          value={String(data.hitl_quality.pending_review_contracts)}
          tone={
            data.hitl_quality.pending_review_contracts > 50
              ? 'warn'
              : data.hitl_quality.pending_review_contracts > 0
                ? 'neutral'
                : 'ok'
          }
          sub="LLM нашёл, юрист ещё не пометил"
        />
        <MetricCard
          caption="Ждут разметки (претензии)"
          value={String(data.hitl_quality.pending_review_claims)}
          tone={
            data.hitl_quality.pending_review_claims > 50
              ? 'warn'
              : data.hitl_quality.pending_review_claims > 0
                ? 'neutral'
                : 'ok'
          }
        />
      </Grid>

      <SectionTitle>Распределение рисков по важности</SectionTitle>
      <Grid>
        <MetricCard
          caption="Критических"
          value={String(data.severity_distribution.critical)}
          tone={data.severity_distribution.critical > 0 ? 'danger' : 'neutral'}
          sub="договоры + претензии"
        />
        <MetricCard
          caption="Важных"
          value={String(data.severity_distribution.major)}
          tone={data.severity_distribution.major > 0 ? 'warn' : 'neutral'}
        />
        <MetricCard
          caption="Незначительных"
          value={String(data.severity_distribution.minor)}
          tone="neutral"
        />
      </Grid>

      {data.top_categories.length > 0 && (
        <>
          <SectionTitle>Топ-5 категорий проблем в договорах</SectionTitle>
          <Grid>
            {data.top_categories.map((c) => (
              <MetricCard
                key={c.category}
                caption={CATEGORY_LABELS[c.category] ?? c.category}
                value={String(c.total)}
                tone={c.critical > 0 ? 'danger' : 'neutral'}
                sub={`Критических: ${c.critical}`}
              />
            ))}
          </Grid>
        </>
      )}

      <SectionTitle>Контрагенты ()</SectionTitle>
      <Grid>
        <MetricCard
          caption="Действующие"
          value={String(data.counterparties.active)}
          tone="ok"
          sub="по данным ЕГРЮЛ"
        />
        <MetricCard
          caption="Под риском"
          value={String(data.counterparties.at_risk)}
          tone={data.counterparties.at_risk > 0 ? 'warn' : 'neutral'}
          sub="ликвидируются / реорганизуются"
        />
        <MetricCard
          caption="Прекратили"
          value={String(data.counterparties.liquidated_or_bankrupt)}
          tone={data.counterparties.liquidated_or_bankrupt > 0 ? 'danger' : 'neutral'}
          sub="ликвидированы / банкроты"
        />
        <MetricCard
          caption="Всего в реестре"
          value={String(data.counterparties.total)}
          tone="neutral"
        />
      </Grid>
    </>
  );
}

const CATEGORY_LABELS: Record<string, string> = {
  subject: 'Предмет',
  price: 'Цена',
  terms: 'Сроки',
  liability: 'Ответственность',
  warranty: 'Гарантии',
  force_majeure: 'Форс-мажор',
  jurisdiction: 'Подсудность',
  other: 'Другое',
};
